// Gerd wird 40: Pauschalen-Prüfung, Gästeliste (Zusagen), WhatsApp-Einladung
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import {
  PartyPopper, RefreshCw, Loader2, CheckCircle2, AlertTriangle, Download, Trash2, Copy, ExternalLink, Users, BedDouble, MessageCircle, ShieldCheck, Mail, Send,
} from "lucide-react";

import { StayList } from "./Gerd40Stay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gerd40`;
const shareUrl = () => "https://schwalbennest-restaurant.de/gerd-wird-40/?code=GERD40";
const shareText = () =>
  `🎉 Ich werde 40 und feiere am Samstag, 24.10.2026 ab 17 Uhr im Schwalbennest auf Rügen – du bist herzlich eingeladen!\n\n` +
  `Hier kannst du zusagen und dir einen Bungalow mit 50 % Geburtstagsrabatt sichern (Code GERD40):\n${shareUrl()}\n\nIch freu mich auf dich! Gerd`;

const euro = (v) => v.toLocaleString("de-DE", { style: "currency", currency: "EUR" });

const Card = ({ children, testid }) => (
  <div className="bg-eb-surface border border-white/5 rounded-xl p-5" data-testid={testid}>{children}</div>
);

const fmt = (iso) => new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Berlin" });

export function WatchChecks({ checks, prefix = "gerd40-watch" }) {
  const groups = [...new Set(checks.map((c) => c.group))];
  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mt-4">
      {groups.map((g) => (
        <div key={g} className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3" data-testid={`${prefix}-group-${g.replace(/\W+/g, "-").toLowerCase()}`}>
          <div className="text-xs uppercase tracking-wider text-eb-muted mb-2">{g}</div>
          {checks.filter((c) => c.group === g).map((c) => (
            <div key={c.label} className={`flex items-start gap-2 text-sm ${c.ok ? "text-emerald-300" : c.hint ? "text-amber-300" : "text-rose-400"}`}>
              {c.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
              <span>{c.label}{c.hint && !c.ok && <span className="text-[10px] uppercase ml-1 opacity-70">Hinweis</span>}
                {(c.hint || !c.ok) && c.detail && <span className="block text-[11px] text-eb-muted">{c.detail}</span>}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function PageWatch() {
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    try { setW((await axios.get(`${API}/watch`)).data); setErr(""); }
    catch (e) { setErr(e.response?.data?.detail || "Wächter-Status konnte nicht geladen werden"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    setBusy(true);
    try { await axios.post(`${API}/watch`); await load(); }
    catch (e) { setErr(e.response?.data?.detail || "Prüfung fehlgeschlagen"); }
    setBusy(false);
  };
  const last = w?.last;
  const ok = last?.status === "ok";
  return (
    <Card testid="gerd40-watch">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold flex items-center gap-2"><ShieldCheck size={18} className="text-eb-primary" /> Seiten-Wächter · alle 15 Minuten</h3>
          <p className="text-xs text-eb-muted mt-1" data-testid="gerd40-watch-meta">
            {w?.url} · Alarm-Mail an {w?.alert_email} · {w?.active ? `aktiv bis ${fmt(w.active_until)}` : "beendet (Feier vorbei)"}
          </p>
        </div>
        <button onClick={run} disabled={busy} data-testid="gerd40-watch-run"
          className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-md bg-eb-primary text-black font-semibold disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Jetzt prüfen
        </button>
      </div>
      {err && <p className="text-sm text-rose-400 mt-3" data-testid="gerd40-watch-error">{err}</p>}
      {!last && !err && <p className="text-sm text-eb-muted mt-3" data-testid="gerd40-watch-empty">Noch keine Prüfung gelaufen.</p>}
      {last && (
        <>
          <div className={`mt-4 flex items-center gap-2 text-sm font-semibold ${ok ? "text-emerald-400" : "text-rose-400"}`} data-testid="gerd40-watch-status">
            {ok ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
            {ok ? "Alles in Ordnung – Seite, Buchung, Pauschalen, Bilder und Formular laufen" : `${last.problems.length} Problem(e) gefunden`}
            {last.checks.some((c) => c.hint && !c.ok) && <span className="text-amber-300 font-normal" data-testid="gerd40-watch-hints">· {last.checks.filter((c) => c.hint && !c.ok).length} Hinweis(e)</span>}
            <span className="text-xs font-normal text-eb-muted">· {fmt(last.at)} ({last.source === "cron" ? "automatisch" : "manuell"})</span>
          </div>
          <WatchChecks checks={last.checks} />
          {last.alert_mail?.sent && <p className="text-[11px] text-emerald-400 mt-3" data-testid="gerd40-watch-mail-sent">Mail an {last.alert_mail.to} verschickt</p>}
          {last.alert_mail?.error && <p className="text-[11px] text-amber-300 mt-3" data-testid="gerd40-watch-mail-error">Mail nicht verschickt: {last.alert_mail.error}</p>}
          {last.dup_mail?.sent && <p className="text-[11px] text-amber-300 mt-1" data-testid="gerd40-watch-dup-mail-sent">Mail zum doppelten Block an {last.dup_mail.to} verschickt</p>}
          {last.dup_mail?.error && <p className="text-[11px] text-amber-300 mt-1" data-testid="gerd40-watch-dup-mail-error">Mail zum doppelten Block nicht verschickt: {last.dup_mail.error}</p>}
          {w.history?.length > 1 && (
            <div className="flex gap-1 mt-3 items-center" data-testid="gerd40-watch-history">
              <span className="text-[11px] text-eb-muted mr-1">Verlauf:</span>
              {w.history.slice().reverse().map((h) => (
                <span key={h.at} title={`${fmt(h.at)} · ${h.status === "ok" ? "ok" : h.problems.join(", ")}`}
                  className={`w-2.5 h-5 rounded-sm ${h.status === "ok" ? "bg-emerald-500/70" : "bg-rose-500/80"}`} />
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function WeeklyReport() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(async () => { setR((await axios.get(`${API}/weekly-report`)).data); }, []);
  useEffect(() => { load().catch(() => setMsg("Wochenbericht konnte nicht geladen werden")); }, [load]);
  const send = async () => {
    setBusy(true); setMsg("");
    try {
      const d = (await axios.post(`${API}/weekly-report`)).data;
      setMsg(d.mails.every((m) => m.sent) ? `Verschickt an ${d.mails.map((m) => m.to).join(" & ")}` : "Mindestens eine Mail ging nicht raus");
      await load();
    } catch (e) { setMsg(e.response?.data?.detail || "Versand fehlgeschlagen"); }
    setBusy(false);
  };
  if (!r) return msg ? <Card testid="gerd40-weekly"><p className="text-sm text-rose-400">{msg}</p></Card> : null;
  return (
    <Card testid="gerd40-weekly">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold flex items-center gap-2"><Mail size={18} className="text-eb-primary" /> Wochenbericht · {r.schedule}</h3>
          <p className="text-xs text-eb-muted mt-1" data-testid="gerd40-weekly-meta">
            an {r.recipients.join(" & ")} · diese Woche: {r.new} neue Zusage(n), {r.open_inquiries} offene Anfrage(n), {r.total.persons} {r.total.persons === 1 ? "Person" : "Personen"} gesamt
            {r.bungalows?.ok && ` · frei: ${r.bungalows.free_1n} (1 Nacht) von ${r.bungalows.units} Bungalows`}
            {r.last && ` · zuletzt verschickt ${fmt(r.last.at)}`}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setOpen(!open)} data-testid="gerd40-weekly-preview" className="text-xs px-3 py-1.5 rounded-md border border-white/15">
            {open ? "Vorschau schließen" : "Vorschau"}
          </button>
          <button onClick={send} disabled={busy} data-testid="gerd40-weekly-send"
            className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-md bg-eb-primary text-black font-semibold disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Jetzt senden
          </button>
        </div>
      </div>
      {msg && <p className="text-xs text-emerald-300 mt-2" data-testid="gerd40-weekly-msg">{msg}</p>}
      {open && <div className="mt-4 rounded-lg bg-white p-4 max-h-96 overflow-auto [&_ul]:list-disc [&_ul]:pl-5 [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-base [&_h3]:font-semibold [&_p]:my-2 [&_a]:underline" data-testid="gerd40-weekly-html" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(r.html) }} />}
    </Card>
  );
}

function GuestReminder() {
  const [r, setR] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(async () => {
    try { setR((await axios.get(`${API}/reminder`)).data); } catch (e) { setMsg("Erinnerung konnte nicht geladen werden"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const test = async () => {
    setBusy(true); setMsg("");
    try {
      const d = (await axios.post(`${API}/reminder/test`)).data;
      setMsg(d.ok ? `Testmail an ${r.test_to} verschickt` : `Testmail fehlgeschlagen: ${d.mail?.error || "unbekannt"}`);
    } catch (e) { setMsg(e.response?.data?.detail || "Testmail fehlgeschlagen"); }
    setBusy(false);
  };
  if (!r) return msg ? <Card testid="gerd40-reminder"><p className="text-sm text-rose-400">{msg}</p></Card> : null;
  const sent = r.last?.sent != null;
  return (
    <Card testid="gerd40-reminder">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold flex items-center gap-2"><Send size={18} className="text-eb-primary" /> Gäste-Erinnerung · {fmt(r.scheduled_at)}</h3>
          <p className="text-xs text-eb-muted mt-1" data-testid="gerd40-reminder-meta">
            geht automatisch an {r.pending} Zusage(n) mit E-Mail · schon erinnert: {r.already_sent}
            {r.without_email > 0 && ` · ${r.without_email} ohne E-Mail (bitte selbst Bescheid geben)`}
            {sent && ` · zuletzt ${fmt(r.last.at)}: ${r.last.sent} verschickt`}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setOpen(!open)} data-testid="gerd40-reminder-preview" className="text-xs px-3 py-1.5 rounded-md border border-white/15">
            {open ? "Vorschau schließen" : "Vorschau"}
          </button>
          <button onClick={test} disabled={busy} data-testid="gerd40-reminder-test"
            className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-md bg-eb-primary text-black font-semibold disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Testmail an mich
          </button>
        </div>
      </div>
      {msg && <p className="text-xs text-emerald-300 mt-2" data-testid="gerd40-reminder-msg">{msg}</p>}
      {open && <div className="mt-4 rounded-lg bg-white p-4 max-h-96 overflow-auto [&_ul]:list-disc [&_ul]:pl-5 [&_h2]:text-xl [&_h2]:font-bold [&_h3]:font-semibold [&_p]:my-2"
        data-testid="gerd40-reminder-html" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(r.html) }} />}
    </Card>
  );
}

function PackageCheck() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = useCallback(async () => {
    setBusy(true);
    try { setRes((await axios.get(`${API}/package-check`)).data); }
    catch (e) { setRes({ error: e.response?.data?.detail || "Prüfung fehlgeschlagen" }); }
    setBusy(false);
  }, []);
  useEffect(() => { run(); }, [run]);
  const ok = res?.bookable;
  return (
    <Card testid="gerd40-package-check">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-heading font-semibold">Pauschale „Gerd wird 40“ · easybooking live</h3>
        <button onClick={run} disabled={busy} data-testid="gerd40-recheck-btn"
          className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-md bg-eb-primary text-black font-semibold disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Erneut prüfen
        </button>
      </div>
      {res?.error && <p className="text-rose-400 text-sm mt-3">{res.error}</p>}
      {res && !res.error && (
        <div className="mt-4 space-y-3">
          <div className={`flex items-center gap-2 text-sm font-semibold ${ok ? "text-emerald-400" : "text-amber-400"}`} data-testid="gerd40-bookable-status">
            {ok ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
            {ok ? "Beide Pauschalen buchbar – Gäste können direkt buchen" : res.variants.some((v) => v.bookable) ? "Teilweise buchbar" : "Noch nicht buchbar"}
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {res.variants.map((v) => (
              <div key={v.nights} data-testid={`gerd40-variant-${v.nights}`}
                className={`rounded-lg px-4 py-3 border ${v.bookable ? "border-emerald-500/30 bg-emerald-500/10" : "border-white/10 bg-white/[0.03]"}`}>
                <div className={`text-sm font-semibold ${v.bookable ? "text-emerald-300" : "text-amber-300"}`}>
                  {v.nights === 1 ? "1 Nacht" : "2 Nächte"} · {v.bookable ? "buchbar ✓" : "nicht buchbar"}
                </div>
                <div className="text-xs text-eb-muted mt-1">{v.label} · {euro(v.ppn)} pro Person und Nacht · 2 Pers. {euro(v.price)} (statt {euro(v.price * 2)}) · jede weitere Person {euro(v.extra)}{v.package_id ? ` · Pauschale ${v.package_id}` : ""}</div>
              </div>
            ))}
          </div>
          {res.issues.length > 0 && (
            <ul className="text-sm space-y-1.5 list-disc pl-5 text-eb-fg/90" data-testid="gerd40-issues">
              {res.issues.map((i) => <li key={i}>{i}</li>)}
            </ul>
          )}
          <p className="text-[11px] text-eb-muted">Geprüft {new Date(res.checked_at).toLocaleString("de-DE")}</p>
        </div>
      )}
    </Card>
  );
}

function WhatsAppShare() {
  const [copied, setCopied] = useState("");
  const copy = async (v, k) => { await navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(""), 2000); };
  return (
    <Card testid="gerd40-whatsapp">
      <h3 className="font-heading font-semibold flex items-center gap-2"><MessageCircle size={16} className="text-emerald-400" /> WhatsApp-Einladung</h3>
      <div className="mt-4 grid md:grid-cols-[260px_1fr] gap-5">
        <img src="/gerd-40/og.jpg" alt="Vorschaubild" className="w-full rounded-lg border border-white/10" data-testid="gerd40-og-preview" />
        <div className="space-y-3">
          <pre className="whitespace-pre-wrap text-sm bg-black/30 rounded-lg p-3 font-sans" data-testid="gerd40-share-text">{shareText()}</pre>
          <div className="flex flex-wrap gap-2">
            <a href={`https://wa.me/?text=${encodeURIComponent(shareText())}`} target="_blank" rel="noreferrer" data-testid="gerd40-wa-btn"
              className="flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-emerald-500 text-black font-semibold hover:bg-emerald-400 transition-colors">
              <MessageCircle size={15} /> In WhatsApp öffnen
            </a>
            <button onClick={() => copy(shareText(), "t")} data-testid="gerd40-copy-text" className="flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-white/10 hover:bg-white/5">
              <Copy size={14} /> {copied === "t" ? "Kopiert!" : "Text kopieren"}
            </button>
            <button onClick={() => copy(shareUrl(), "l")} data-testid="gerd40-copy-link" className="flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-white/10 hover:bg-white/5">
              <Copy size={14} /> {copied === "l" ? "Kopiert!" : "Nur Link"}
            </button>
            <a href="/gerd-40/divi-anleitung.html" target="_blank" rel="noreferrer" data-testid="gerd40-divi-guide" className="flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-white/10 hover:bg-white/5">
              <ExternalLink size={14} /> WordPress-Code (Restaurant-Seite)
            </a>
            <a href="/gerd-40/" target="_blank" rel="noreferrer" data-testid="gerd40-open-page" className="flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-white/10 hover:bg-white/5">
              <ExternalLink size={14} /> Seite öffnen
            </a>
          </div>
          <p className="text-[11px] text-eb-muted">Der Link enthält den Code – der Rabatt ist für Gäste sofort freigeschaltet. WhatsApp zeigt das Vorschaubild automatisch.</p>
        </div>
      </div>
    </Card>
  );
}

function GuestList() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => setData((await axios.get(`${API}/rsvps`)).data), []);
  useEffect(() => { load(); }, [load]);
  const del = async (r) => {
    if (!window.confirm(`Zusage von ${r.name} löschen?`)) return;
    await axios.delete(`${API}/rsvps/${r.id}`);
    load();
  };
  const csv = async () => {
    const r = await axios.get(`${API}/rsvps.csv`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = "gerd40-gaesteliste.csv";
    a.click();
  };
  if (!data) return <Card><Loader2 className="animate-spin" size={18} /></Card>;
  const t = data.totals;
  return (
    <Card testid="gerd40-guestlist">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-heading font-semibold">Gästeliste</h3>
        <div className="flex gap-2">
          <button onClick={load} data-testid="gerd40-reload" className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5"><RefreshCw size={13} /> Aktualisieren</button>
          <button onClick={csv} data-testid="gerd40-csv" className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5"><Download size={13} /> CSV</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        {[["Zusagen", t.rsvps, "gerd40-total-rsvps"], ["Personen", t.persons, "gerd40-total-persons"],
          ["davon Kinder", t.children, "gerd40-total-children"], ["mit Übernachtung", t.overnight_persons, "gerd40-total-overnight"]].map(([l, v, id]) => (
          <div key={id} className="bg-black/25 rounded-lg p-3">
            <div className="text-2xl font-heading font-bold text-eb-primary" data-testid={id}>{v}</div>
            <div className="text-[11px] text-eb-muted uppercase tracking-wider">{l}</div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-eb-muted mt-3">Bei jeder neuen Zusage geht eine Nachricht an {data.notify_to}.</p>
      {data.rows.length === 0 ? (
        <p className="text-sm text-eb-muted mt-4" data-testid="gerd40-empty">Noch keine Zusagen – verschick die WhatsApp-Einladung.</p>
      ) : (
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm" data-testid="gerd40-rsvp-table">
            <thead className="text-[11px] uppercase tracking-wider text-eb-muted text-left">
              <tr><th className="py-2 pr-3">Name</th><th className="pr-3"><Users size={13} /></th><th className="pr-3"><BedDouble size={13} /></th><th className="pr-3">Kontakt</th><th className="pr-3">Nachricht</th><th className="pr-3">Mail</th><th /></tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 align-top" data-testid={`gerd40-row-${r.id}`}>
                  <td className="py-2 pr-3 font-medium">{r.name}<div className="text-[11px] text-eb-muted">{new Date(r.created_at).toLocaleString("de-DE")}</div></td>
                  <td className="pr-3">{r.adults}{r.children ? ` + ${r.children} K` : ""}</td>
                  <td className="pr-3">{r.overnight ? "ja" : "nein"}</td>
                  <td className="pr-3 text-xs">{r.email}<div>{r.phone}</div></td>
                  <td className="pr-3 text-xs max-w-[260px]">{r.message}</td>
                  <td className="pr-3 text-xs">{r.mail?.sent ? <span className="text-emerald-400">✓</span> : <span className="text-amber-400" title={r.mail?.error}>!</span>}</td>
                  <td><button onClick={() => del(r)} data-testid={`gerd40-del-${r.id}`} className="text-eb-muted hover:text-rose-400"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

const STATUS = { neu: "bg-amber-500/15 text-amber-300", gebucht: "bg-emerald-500/15 text-emerald-300", abgelehnt: "bg-white/5 text-eb-muted" };

function Inquiries() {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => setRows((await axios.get(`${API}/inquiries`)).data), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (r, status) => { await axios.patch(`${API}/inquiries/${r.id}`, { status }); load(); };
  const del = async (r) => {
    if (!window.confirm(`Anfrage von ${r.name} löschen?`)) return;
    await axios.delete(`${API}/inquiries/${r.id}`);
    load();
  };
  if (!rows) return <Card><Loader2 className="animate-spin" size={18} /></Card>;
  return (
    <Card testid="gerd40-inquiries">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-heading font-semibold">Bungalow-Anfragen</h3>
        <button onClick={load} data-testid="gerd40-inq-reload" className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5"><RefreshCw size={13} /> Aktualisieren</button>
      </div>
      <p className="text-[11px] text-eb-muted mt-1">Jede Person ab 13 Jahren = Pauschalenpreis pro Nacht (live aus easybooking) × Nächte · Kinder bis 12 kostenlos · bitte in easybooking von Hand buchen</p>
      {rows.length === 0 ? (
        <p className="text-sm text-eb-muted mt-4" data-testid="gerd40-inq-empty">Noch keine Anfragen.</p>
      ) : (
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm" data-testid="gerd40-inq-table">
            <thead className="text-[11px] uppercase tracking-wider text-eb-muted text-left">
              <tr><th className="py-2 pr-3">Name</th><th className="pr-3">Nächte</th><th className="pr-3">Personen</th><th className="pr-3">Preis</th><th className="pr-3">Kontakt</th><th className="pr-3">Nachricht</th><th className="pr-3">Status</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 align-top" data-testid={`gerd40-inq-row-${r.id}`}>
                  <td className="py-2 pr-3 font-medium">{r.name}<div className="text-[11px] text-eb-muted">{new Date(r.created_at).toLocaleString("de-DE")}</div></td>
                  <td className="pr-3">{r.nights || 2}</td>
                  <td className="pr-3">{r.adults}{r.children ? ` + ${r.children} K` : ""}</td>
                  <td className="pr-3 font-semibold text-eb-primary">{euro(r.price)}</td>
                  <td className="pr-3 text-xs">{r.email}<div>{r.phone}</div></td>
                  <td className="pr-3 text-xs max-w-[240px]">{r.message}</td>
                  <td className="pr-3">
                    <select value={r.status} onChange={(e) => setStatus(r, e.target.value)} data-testid={`gerd40-inq-status-${r.id}`}
                      className={`text-xs rounded px-2 py-1 border-0 ${STATUS[r.status]}`}>
                      <option value="neu">neu</option><option value="gebucht">gebucht</option><option value="abgelehnt">abgelehnt</option>
                    </select>
                  </td>
                  <td><button onClick={() => del(r)} data-testid={`gerd40-inq-del-${r.id}`} className="text-eb-muted hover:text-rose-400"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export function Gerd40Panel() {
  return (
    <div className="space-y-5" data-testid="gerd40-view">
      <div>
        <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><PartyPopper size={22} className="text-eb-primary" /> Gerd wird 40</h2>
        <p className="text-eb-muted text-sm">Einladungsseite · Zusagen · Pauschale mit Code GERD40 (Strandhus · Kunde 11699)</p>
      </div>
      <PageWatch />
      <WeeklyReport />
      <GuestReminder />
      <PackageCheck />
      <GuestList />
      <StayList />
      <Inquiries />
      <WhatsAppShare />
    </div>
  );
}
